import { createContext, useContext } from 'react';

/**
 * wizardStepContext — the per-step accessible-name channel, extracted so BOTH
 * frames can provide it.
 *
 * WHY THIS FILE EXISTS. In the Design this context is declared in
 * `pages/UploadWizard.tsx`, the one frame that provides it, and `ModalShell`
 * imports it straight from there. The backend now has TWO frames that render a
 * ModalShell:
 *
 *   - `pages/UploadWizard.tsx` — the copied Design frame, for the upload steps.
 *   - `design/OverlayFrame.tsx` — the earlier hand-written frame, still the shell
 *     for `FlowPickerReskin` until the copied `UploadSelectPage` replaces it.
 *
 * Declaring the context in either one would give the other its own separate
 * `createContext` object. `ModalShell` can only consume ONE of them, so a step
 * mounted in the other frame would read a context that never receives a provider,
 * silently fall back to the inert default, and the dialog would go UNNAMED — a
 * real a11y regression with no visible symptom. Hence one module, imported by all
 * three.
 *
 * The shape is VERBATIM from the Design's UploadWizard; only its address changed.
 */

/**
 * The per-step accessible name a ModalShell reports up to the persistent frame.
 * P1 points at its visible title (`labelledBy`); P2–P5 have no title (`label`).
 */
export interface WizardModalName {
  labelledBy?: string;
  label?: string;
}

/**
 * What a frame hands down to the mounted step. The `role="dialog"` element is the
 * frame's ONE persistent box (so it never remounts between steps), so the step
 * does not register a box node — it registers only its NAME, which the frame
 * renders as `aria-labelledby`/`aria-label`.
 */
export interface WizardStepValue {
  setModalName: (name: WizardModalName) => void;
}

/**
 * The default is inert (a no-op) so a ModalShell rendered outside any frame simply
 * goes unnamed rather than throwing.
 */
export const WizardStepContext = createContext<WizardStepValue>({
  setModalName: () => {},
});

/**
 * Consumed by each step's ModalShell to report its accessible name up to the
 * persistent frame. Returns the setter, called in an effect on mount / when the
 * name changes.
 */
export function useWizardModalName() {
  return useContext(WizardStepContext).setModalName;
}
